import { AlertTriangle, XCircle } from "lucide-react";
import { accessibilityTools, type ColorBlindnessType } from "../../lib/accessibilityTools";

const SIMULATION_TYPES: Array<{
  value: ColorBlindnessType;
  label: string;
  note: string;
}> = [
  { value: "protanopia", label: "Protanopia", note: "Red cones absent" },
  { value: "deuteranopia", label: "Deuteranopia", note: "Green cones absent" },
  { value: "tritanopia", label: "Tritanopia", note: "Blue cones absent" },
  { value: "protanomaly", label: "Protanomaly", note: "Red cone weakness" },
  { value: "deuteranomaly", label: "Deuteranomaly", note: "Green cone weakness" },
  { value: "tritanomaly", label: "Tritanomaly", note: "Blue cone weakness" },
  { value: "achromatomaly", label: "Achromatomaly", note: "Generalized color weakness" },
  { value: "achromatopsia", label: "Achromatopsia", note: "No color perception" },
];

export interface ConfusablePair {
  first: string;
  second: string;
  deltaE: number;
}

const describeConfusableSeverity = (
  deltaE: number,
): { label: string; detail: string } => {
  if (deltaE < 1) {
    return {
      label: "Critical risk",
      detail:
        "These colors are virtually indistinguishable. Select entirely different hues or add non-color cues.",
    };
  }
  if (deltaE < 2.5) {
    return {
      label: "Severe risk",
      detail:
        "Colors remain highly confusable across most simulations. Increase hue and luminance separation immediately.",
    };
  }
  if (deltaE < 4.5) {
    return {
      label: "Moderate risk",
      detail:
        "Users with low vision or color blindness may still confuse this pair. Consider bolder value or saturation changes.",
    };
  }
  return {
    label: "Mild risk",
    detail:
      "This pair is close under the current ΔE threshold. Add complementary cues or widen the color distance further.",
  };
};

interface ConfusableOverlayProps {
  pair: ConfusablePair;
  onDismiss: () => void;
}

export function ConfusableOverlay({ pair, onDismiss }: ConfusableOverlayProps) {
  const simulations = SIMULATION_TYPES.map((type) => ({
    type,
    first:
      accessibilityTools.simulateColorBlindness(pair.first, type.value) ??
      pair.first,
    second:
      accessibilityTools.simulateColorBlindness(pair.second, type.value) ??
      pair.second,
  }));

  const severity = describeConfusableSeverity(pair.deltaE);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Confusable Pair Simulations
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {pair.first} · {pair.second} · ΔE {pair.deltaE.toFixed(2)}
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close confusable pair simulators"
          >
            <XCircle className="w-4 h-4" /> Close
          </button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 space-y-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Original Colors
              </p>
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded border border-gray-200 dark:border-gray-600"
                  style={{ backgroundColor: pair.first }}
                />
                <div
                  className="w-12 h-12 rounded border border-gray-200 dark:border-gray-600"
                  style={{ backgroundColor: pair.second }}
                />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                ΔE2000 distance of {pair.deltaE.toFixed(2)} indicates these
                colors may be indistinguishable in some contexts.
              </p>
            </div>
            <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2 bg-blue-50/60 dark:bg-blue-900/20">
              <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 uppercase">
                Guidance
              </p>
              <div className="space-y-2">
                <div className="pal-confusable-severity text-xs text-yellow-700 dark:text-yellow-200">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold uppercase tracking-wide">
                      {severity.label}
                    </p>
                    <p>{severity.detail}</p>
                  </div>
                </div>
              </div>
              <ul className="list-disc list-inside text-xs text-blue-800 dark:text-blue-200 space-y-1">
                <li>Increase contrast by choosing hues with higher ΔE values.</li>
                <li>Pair with distinct value/brightness differences to aid low-vision users.</li>
                <li>Use patterns or labels when color alone cannot communicate meaning.</li>
                <li>Re-run the audit after adjustments to validate improvements.</li>
              </ul>
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Color Blindness Simulations
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {simulations.map((simulation) => (
                <div
                  key={simulation.type.value}
                  className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 space-y-3 bg-gray-50 dark:bg-gray-800/60"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {simulation.type.label}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {simulation.type.note}
                      </p>
                    </div>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      ΔE {pair.deltaE.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        First Color
                      </p>
                      <div
                        className="w-full h-16 rounded border border-gray-200 dark:border-gray-700"
                        style={{ backgroundColor: simulation.first }}
                      />
                      <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400 font-mono">
                        {simulation.first}
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Second Color
                      </p>
                      <div
                        className="w-full h-16 rounded border border-gray-200 dark:border-gray-700"
                        style={{ backgroundColor: simulation.second }}
                      />
                      <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400 font-mono">
                        {simulation.second}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
